import { useEffect, useMemo, useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';

const AREA_OPTIONS = [
  { value: 'WH', label: 'WH - Main warehouse' },
  { value: 'KT', label: 'KT - Kitchen storage' },
  { value: 'RM1', label: 'RM1 - Room 1' },
  { value: 'F2', label: 'F2 - Second floor filter area' },
  { value: 'RM2', label: 'RM2 - Room 2 (optional legacy area)' },
  { value: 'SH', label: 'SH - Showroom / special holding (optional)' },
];

const pad = (value, size = 2) => String(value || '').replace(/\D/g, '').padStart(size, '0');

const parseLocation = (location = '') => {
  const shelvedNoLevel = String(location || '').match(/^([A-Z0-9]+)-S(\d+)-C(\d+)$/i);
  if (shelvedNoLevel) {
    return {
      mode: 'shelved',
      area: shelvedNoLevel[1].toUpperCase(),
      section: shelvedNoLevel[2],
      column: shelvedNoLevel[3],
    };
  }

  const shelved = String(location || '').match(/^([A-Z0-9]+)-S(\d+)-L(\d+)-C(\d+)$/i);
  if (shelved) {
    return {
      mode: 'shelved',
      area: shelved[1].toUpperCase(),
      section: shelved[2],
      column: shelved[4],
    };
  }

  const floor = String(location || '').match(/^([A-Z0-9]+)-Z(\d+)-P(\d+)$/i);
  if (floor) {
    return {
      mode: 'floor',
      area: floor[1].toUpperCase(),
      zone: floor[2],
      pile: floor[3],
    };
  }

  return null;
};

const WarehouseLocationBuilder = ({ currentLocation, onApply }) => {
  const [mode, setMode] = useState('shelved');
  const [area, setArea] = useState('WH');
  const [section, setSection] = useState('');
  const [column, setColumn] = useState('');
  const [zone, setZone] = useState('');
  const [pile, setPile] = useState('');

  useEffect(() => {
    const parsed = parseLocation(currentLocation);
    if (!parsed) return;
    setMode(parsed.mode || 'shelved');
    setArea(parsed.area || 'WH');
    setSection(parsed.section || '');
    setColumn(parsed.column || '');
    setZone(parsed.zone || '');
    setPile(parsed.pile || '');
  }, [currentLocation]);

  const preview = useMemo(() => {
    if (mode === 'shelved') {
      return `${area}-S${pad(section)}-C${pad(column)}`;
    }
    return `${area}-Z${pad(zone)}-P${pad(pile)}`;
  }, [mode, area, section, column, zone, pile]);

  return (
    <div className='border rounded p-2 mt-2 bg-light'>
      <div className='small fw-semibold mb-2'>Quick location builder (optional)</div>
      <Row className='g-2 align-items-end'>
        <Col md={3}>
          <Form.Label className='mb-1 small'>Area</Form.Label>
          <Form.Select value={area} onChange={(e) => setArea(e.target.value)}>
            {AREA_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </Form.Select>
        </Col>
        <Col md={3}>
          <Form.Label className='mb-1 small'>Storage</Form.Label>
          <Form.Select value={mode} onChange={(e) => setMode(e.target.value)}>
            <option value='shelved'>Shelved (SEC-COL)</option>
            <option value='floor'>Floor stack (ZONE-PILE)</option>
          </Form.Select>
        </Col>

        {mode === 'shelved' ? (
          <>
            <Col md={2}>
              <Form.Label className='mb-1 small'>Section</Form.Label>
              <Form.Control value={section} onChange={(e) => setSection(e.target.value)} placeholder='03' />
            </Col>
            <Col md={2}>
              <Form.Label className='mb-1 small'>Column</Form.Label>
              <Form.Control value={column} onChange={(e) => setColumn(e.target.value)} placeholder='04' />
            </Col>
          </>
        ) : (
          <>
            <Col md={3}>
              <Form.Label className='mb-1 small'>Zone</Form.Label>
              <Form.Control value={zone} onChange={(e) => setZone(e.target.value)} placeholder='01' />
            </Col>
            <Col md={3}>
              <Form.Label className='mb-1 small'>Pile</Form.Label>
              <Form.Control value={pile} onChange={(e) => setPile(e.target.value)} placeholder='01' />
            </Col>
          </>
        )}
      </Row>

      <div className='d-flex justify-content-between align-items-center mt-2'>
        <div className='small text-muted'>Preview: <code>{preview}</code></div>
        <Button type='button' size='sm' variant='outline-primary' onClick={() => onApply(preview)}>
          Use this location
        </Button>
      </div>
    </div>
  );
};

export default WarehouseLocationBuilder;
